import { useEffect, useState } from "react";
import { getMe, updatePreferences, updateProfile } from "../api/endpoints";
import { Button, Card, ErrorMessage, Input, PageHeading, Select, Spinner } from "../components/ui";
import { useAsync } from "../hooks/useAsync";

export default function SettingsPage() {
  const { data, error, loading, reload } = useAsync(getMe, []);
  const [profile, setProfile] = useState({ displayName: "", timezone: "", weightUnit: "kg" as "kg" | "lb" });
  const [prefs, setPrefs] = useState({ easyPaceBaselineSecPerKm: "", maxHeartRateBpm: "", restingHeartRateBpm: "", lactateThresholdHr: "", ageBorn: "", enableRunningFeatures: true });
  const [profileError, setProfileError] = useState<string | null>(null);
  const [prefsError, setPrefsError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setProfile({ displayName: data.displayName ?? "", timezone: data.timezone, weightUnit: (data.weightUnit as "kg" | "lb") ?? "kg" });
    setPrefs({
      easyPaceBaselineSecPerKm: data.easyPaceBaselineSecPerKm != null ? String(data.easyPaceBaselineSecPerKm) : "",
      maxHeartRateBpm: data.maxHeartRateBpm != null ? String(data.maxHeartRateBpm) : "",
      restingHeartRateBpm: data.restingHeartRateBpm != null ? String(data.restingHeartRateBpm) : "",
      lactateThresholdHr: data.lactateThresholdHr != null ? String(data.lactateThresholdHr) : "",
      ageBorn: data.ageBorn != null ? String(data.ageBorn) : "",
      enableRunningFeatures: data.enableRunningFeatures,
    });
  }, [data]);

  async function saveProfile() {
    setBusy("profile");
    setProfileError(null);
    try {
      await updateProfile(profile);
      reload();
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function savePrefs() {
    setBusy("prefs");
    setPrefsError(null);
    try {
      await updatePreferences({
        easyPaceBaselineSecPerKm: prefs.easyPaceBaselineSecPerKm ? Number(prefs.easyPaceBaselineSecPerKm) : null,
        maxHeartRateBpm: prefs.maxHeartRateBpm ? Number(prefs.maxHeartRateBpm) : null,
        restingHeartRateBpm: prefs.restingHeartRateBpm ? Number(prefs.restingHeartRateBpm) : null,
        lactateThresholdHr: prefs.lactateThresholdHr ? Number(prefs.lactateThresholdHr) : null,
        ageBorn: prefs.ageBorn ? Number(prefs.ageBorn) : null,
        enableRunningFeatures: prefs.enableRunningFeatures,
      });
      reload();
    } catch (err) {
      setPrefsError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorMessage error={error ?? new Error("Failed to load settings")} onRetry={reload} />;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeading title="Settings" subtitle="Profile and running preferences." />

      <Card className="p-6">
        <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Profile</h2>
        {profileError && <div className="mt-3"><ErrorMessage error={new Error(profileError)} /></div>}
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Display name</span>
            <Input value={profile.displayName} onChange={(event) => setProfile({ ...profile, displayName: event.target.value })} data-testid="settings-display-name-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Timezone</span>
            <Input value={profile.timezone} onChange={(event) => setProfile({ ...profile, timezone: event.target.value })} data-testid="settings-timezone-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Weight unit</span>
            <Select value={profile.weightUnit} onChange={(event) => setProfile({ ...profile, weightUnit: event.target.value as "kg" | "lb" })} data-testid="settings-weight-unit-input">
              <option value="kg">kg</option>
              <option value="lb">lb</option>
            </Select>
          </label>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => void saveProfile()} disabled={busy !== null} data-testid="settings-profile-save-button">Save profile</Button>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Running preferences</h2>
        {prefsError && <div className="mt-3"><ErrorMessage error={new Error(prefsError)} /></div>}
        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Easy pace baseline (sec/km)</span>
            <Input type="number" value={prefs.easyPaceBaselineSecPerKm} onChange={(event) => setPrefs({ ...prefs, easyPaceBaselineSecPerKm: event.target.value })} data-testid="settings-easy-pace-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Max heart rate</span>
            <Input type="number" value={prefs.maxHeartRateBpm} onChange={(event) => setPrefs({ ...prefs, maxHeartRateBpm: event.target.value })} data-testid="settings-max-heart-rate-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Resting heart rate</span>
            <Input type="number" value={prefs.restingHeartRateBpm} onChange={(event) => setPrefs({ ...prefs, restingHeartRateBpm: event.target.value })} data-testid="settings-resting-heart-rate-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Lactate threshold HR</span>
            <Input type="number" value={prefs.lactateThresholdHr} onChange={(event) => setPrefs({ ...prefs, lactateThresholdHr: event.target.value })} data-testid="settings-lactate-threshold-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Birth year</span>
            <Input type="number" value={prefs.ageBorn} onChange={(event) => setPrefs({ ...prefs, ageBorn: event.target.value })} data-testid="settings-age-born-input" />
          </label>
          <label className="flex items-end gap-3 text-sm text-gray-600 dark:text-gray-300">
            <input type="checkbox" checked={prefs.enableRunningFeatures} onChange={(event) => setPrefs({ ...prefs, enableRunningFeatures: event.target.checked })} data-testid="settings-enable-running-features-input" />
            <span>Enable running features</span>
          </label>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => void savePrefs()} disabled={busy !== null} data-testid="settings-preferences-save-button">Save preferences</Button>
        </div>
      </Card>
    </div>
  );
}
